'use client';
import React, { useState } from 'react';
import { useForm } from 'react-hook-form';
import { toast } from 'sonner';
import { Send } from 'lucide-react';

const ENTRIES = [
  { id: 'gb-001', author: 'Melissa Torres', avatar: 'MT', date: 'June 20, 2026', message: 'Biscuit is the sweetest soul on the internet. Those beach photos made my entire week. Sending so much love from Florida! 🐾❤️' },
  { id: 'gb-002', author: 'James & Cooper', avatar: 'JC', date: 'June 18, 2026', message: 'Cooper (our golden) and Biscuit would be best friends! We love following her adventures. Happy belated birthday, Biscuit!' },
  { id: 'gb-003', author: 'Priya Nair', avatar: 'PN', date: 'June 15, 2026', message: 'I found DoggyYears through Biscuit\'s profile and immediately signed up for my own dog. She is an absolute legend. 🏆' },
  { id: 'gb-004', author: 'David & Luna', avatar: 'DL', date: 'June 12, 2026', message: 'The agility graduation memory made me cry. You can see how proud she is in that photo. What a wonderful dog.' },
  { id: 'gb-005', author: 'Rachel Kim', avatar: 'RK', date: 'June 8, 2026', message: 'Dog of the Day well deserved!! Biscuit has been my favorite profile since day one. Giving all the paws today 🐾🐾🐾' },
];

interface GuestbookForm { name: string; message: string; }

export default function DogGuestbook() {
  const [entries, setEntries] = useState(ENTRIES);
  const { register, handleSubmit, reset, formState: { errors, isSubmitting } } = useForm<GuestbookForm>();

  const onSubmit = async (data: GuestbookForm) => {
    await new Promise(r => setTimeout(r, 800));
    const newEntry = {
      id: `gb-new-${entries.length + 1}`,
      author: data.name,
      avatar: data.name.slice(0, 2).toUpperCase(),
      date: 'Just now',
      message: data.message,
    };
    setEntries(e => [newEntry, ...e]);
    reset();
    toast.success('Your message has been added to the guestbook!');
  };

  return (
    <div className="max-w-2xl mx-auto">
      <div className="flex items-center justify-between mb-6">
        <h2 className="text-xl font-700 text-foreground">Guestbook</h2>
        <span className="text-sm text-muted-foreground">384 messages</span>
      </div>

      {/* Sign form */}
      <form onSubmit={handleSubmit(onSubmit)} className="bg-secondary border border-primary/10 rounded-2xl p-5 mb-8">
        <h3 className="font-700 text-sm text-foreground mb-4">Leave a message for Biscuit</h3>
        <div className="space-y-3">
          <div>
            <label className="block text-xs font-600 text-foreground mb-1">Your Name</label>
            <input
              {...register('name', { required: 'Please enter your name' })}
              className="w-full px-3 py-2 bg-card border border-border rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-ring"
              placeholder="e.g. Emma & Max"
            />
            {errors.name && <p className="text-xs text-danger mt-1">{errors.name.message}</p>}
          </div>
          <div>
            <label className="block text-xs font-600 text-foreground mb-1">Message</label>
            <textarea
              {...register('message', { required: 'Please write a message', minLength: { value: 10, message: 'Message must be at least 10 characters' } })}
              rows={3}
              className="w-full px-3 py-2 bg-card border border-border rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-ring resize-none"
              placeholder="Share your love for Biscuit..."
            />
            {errors.message && <p className="text-xs text-danger mt-1">{errors.message.message}</p>}
          </div>
          <button
            type="submit"
            disabled={isSubmitting}
            className="paw-btn flex items-center gap-2 px-5 py-2 bg-primary text-primary-foreground rounded-lg text-sm font-700 hover:bg-secondary-foreground transition-all disabled:opacity-60"
          >
            {isSubmitting ? (
              <span className="flex items-center gap-2"><span className="w-3 h-3 border-2 border-white/30 border-t-white rounded-full animate-spin" />Signing...</span>
            ) : (
              <><Send size={14} />Sign Guestbook</>
            )}
          </button>
        </div>
      </form>

      {/* Entries */}
      <div className="space-y-4">
        {entries.map((entry) => (
          <div key={entry.id} className="bg-card border border-border rounded-xl p-4 shadow-card fade-in">
            <div className="flex items-start gap-3">
              <div className="w-9 h-9 rounded-full gradient-violet flex items-center justify-center shrink-0">
                <span className="text-white text-xs font-700">{entry.avatar}</span>
              </div>
              <div className="flex-1 min-w-0">
                <div className="flex items-center justify-between gap-2 mb-1">
                  <p className="font-700 text-sm text-foreground">{entry.author}</p>
                  <p className="text-xs text-muted-foreground shrink-0">{entry.date}</p>
                </div>
                <p className="text-sm text-foreground/80 leading-relaxed">{entry.message}</p>
              </div>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}